import type { ChangePasswordDTO } from '@conversed/shared/src/central/dto/account.dto';
import type {
  AdminMFAForCompanyReqDTO,
  AdminMFAForUserReqDTO,
  DisableMFAReqDTO,
  EnableMFAReqDTO,
  InitiateMFAResDTO,
} from '@conversed/shared/src/central/dto/mfa.dto';
import type {
  MicrosoftSSOCallbackReqDTO,
  MicrosoftSSOInitiateResDTO,
  MicrosoftSSOTokenResDTO,
} from '@conversed/shared/src/central/dto/microsoft-sso.dto';
import type {
  TDTOGetCurrentUserResponse,
  UserProfilePictureUpdateReqDTO,
  UserProfilePictureUpdateResDTO,
  UserProfileUpdateDTO,
} from '@conversed/shared/src/central/dto/user.dto';
import { ENDPOINTS } from '../constants/apiEndpoints';
import {
  formatLoginPayload,
  formatRefreshTokenPayload,
} from '../helpers/apiHelper';
import type { IUserLoginPayload } from '../interfaces/ILogin';
import type { ICheckResetPasswordPayload } from '../interfaces/payloads/ICheckResetPasswordPayload';
import type { IForgotPasswordPayload } from '../interfaces/payloads/IForgotPasswordPayload';
import type { IPasswordResetPayload } from '../interfaces/payloads/IPasswordResetPayload';
import { axiosAuth, axiosDefault } from './axios/axios';
import { axiosInterceptAccessTokenExpiry } from './axios/axiosIntercepted';
import type { TGeneralResponse } from './dto/common.dto';

export const loginApi = (payload: IUserLoginPayload): TGeneralResponse<any> =>
  axiosAuth().post(ENDPOINTS.OAUTH_LOGIN, formatLoginPayload(payload));

export const refreshAccessTokenApi = (token: string): TGeneralResponse<any> =>
  axiosAuth().post(ENDPOINTS.OAUTH_LOGIN, formatRefreshTokenPayload(token));

export const sendPasswordResetEmailApi = (
  payload: IForgotPasswordPayload,
): TGeneralResponse<any> =>
  axiosDefault().post(ENDPOINTS.FORGOT_PASSWORD, payload, {});

export const checkResetCodeApi = (
  payload: ICheckResetPasswordPayload,
): TGeneralResponse<any> =>
  axiosDefault().post(ENDPOINTS.CHECK_RESET_PASSWORD_TOKEN, payload, {});

export const resetPasswordApi = (
  payload: IPasswordResetPayload,
): TGeneralResponse<any> =>
  axiosDefault().post(ENDPOINTS.RESET_PASSWORD, payload, {});

export const meApi = (): TGeneralResponse<TDTOGetCurrentUserResponse> =>
  axiosInterceptAccessTokenExpiry().get(ENDPOINTS.USER_DATA);

export const getPermissionsApi = (): TGeneralResponse<any> =>
  axiosInterceptAccessTokenExpiry().get(ENDPOINTS.USER_ROUTE);

export const updateProfileApi = (
  profile: UserProfileUpdateDTO,
): TGeneralResponse<UserProfileUpdateDTO> =>
  axiosInterceptAccessTokenExpiry().put(ENDPOINTS.PROFILE, profile);

export const updateProfilePictureApi = (
  profilePic: UserProfilePictureUpdateReqDTO,
): TGeneralResponse<UserProfilePictureUpdateResDTO> =>
  axiosInterceptAccessTokenExpiry().post(
    `${ENDPOINTS.PROFILE}/picture`,
    profilePic,
  );

export const initMFAApi = (): TGeneralResponse<InitiateMFAResDTO> =>
  axiosInterceptAccessTokenExpiry().post(`${ENDPOINTS.MFA}/init`);

export const enableMFAApi = (data: EnableMFAReqDTO): TGeneralResponse =>
  axiosInterceptAccessTokenExpiry().post(`${ENDPOINTS.MFA}/enable`, data);

export const disableMFAApi = (data: DisableMFAReqDTO): TGeneralResponse =>
  axiosInterceptAccessTokenExpiry().post(`${ENDPOINTS.MFA}/disable`, data);

export const adminDisableMFAForMemberApi = (
  data: AdminMFAForUserReqDTO,
): TGeneralResponse<any> =>
  axiosInterceptAccessTokenExpiry().post(
    `${ENDPOINTS.MFA}/admin/disable/user`,
    data,
  );

export const adminEnforceMFAForCompanyApi = (
  data: AdminMFAForCompanyReqDTO,
): TGeneralResponse =>
  axiosInterceptAccessTokenExpiry().post(
    `${ENDPOINTS.MFA}/admin/enforce/everyone`,
    data,
  );

export const updatePasswordApi = (data: ChangePasswordDTO): TGeneralResponse =>
  axiosInterceptAccessTokenExpiry().post(`${ENDPOINTS.CHANGE_PASSWORD}`, data);

export const initiateMicrosoftSSOApi =
  (): TGeneralResponse<MicrosoftSSOInitiateResDTO> =>
    axiosDefault().get('/auth/microsoft/login');

export const completeMicrosoftSSOApi = (
  payload: MicrosoftSSOCallbackReqDTO,
): TGeneralResponse<MicrosoftSSOTokenResDTO> =>
  axiosDefault().post('/auth/microsoft/callback', payload);
